import { createContext, useContext, useState } from "react";

const LangContext = createContext({ lang: "en", toggle: () => {} });

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("smf_lang") || "en");
  const toggle = () =>
    setLang((l) => {
      const n = l === "en" ? "ru" : "en";
      localStorage.setItem("smf_lang", n);
      return n;
    });
  return <LangContext.Provider value={{ lang, toggle }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const loc = (obj, field, lang) => (lang === "ru" && obj?.[`${field}_ru`]) || obj?.[field] || "";
export const locList = (obj, field, lang) =>
  (lang === "ru" && obj?.[`${field}_ru`]?.length ? obj[`${field}_ru`] : obj?.[field]) || [];

export const T = {
  en: {
    nav: { home: "Home", gala: "Gala", afterparty: "Afterparty", sunset: "First Sunset", buy: "Buy Tickets" },
    hero: {
      kicker: "Dec 28, 2026 — Jan 3, 2027 · Saint Martin",
      lines: ["Vse Nashi Saint Martin", "New Year Celebration"],
      sub: "Presented by America Bez Granitz TV",
    },
    marquee: ["New Year Gala 2027", "Official After Party", "First Sunrise", "First Sunset", "Six events · Seven days", "Saint Martin", "Vse Nashi SXM"],
    countdown: { title: "Countdown to the Gala", days: "Days", hours: "Hours", min: "Min", sec: "Sec" },
    manifesto: {
      kicker: "Why Saint Martin",
      title1: "Welcome the New Year",
      titleAccent: "in a way",
      title2: "you never have before.",
      chapters: [
        {
          title: "The Island",
          text: "Saint Martin is where jets skim the beach at Maho, where French cuisine meets Caribbean rhythm, and where December feels like a perfect July. We chose it because no ballroom on Earth competes with this shoreline.",
        },
        {
          title: "The Sound",
          text: "Irakli with «London–Paris», «Kapli Absenta» and a special New Year performance. Johnny Versace with Afrobeats, dance and global hits. Douglas — the legendary Italian singer, DJ Dr. Rayham, DJ Kasbi and special guests. One continuous soundtrack of your New Year.",
        },
        {
          title: "The Ritual",
          text: "Moët & Chandon and fireworks over the sea at midnight at Kalos. The first sunrise of 2027 at Villa Aura, champagne in hand. And the first sunset of the year by the pool. This is not a party you attend — it's the way you'll remember starting the year.",
        },
      ],
    },
    events: {
      kicker: "The Program",
      title1: "Signature",
      title2: "events.",
      onePass: "One pass · All events",
      view: "View event",
      more: "…and more special events at the island's best venues — to be announced very soon.",
    },
    finalCta: { kicker: "Limited capacity", title1: "Start 2027 where", titleAccent: "the sun does.", buy: "Buy Tickets" },
    packages: {
      kicker: "Tickets & Packages",
      title1: "Choose your",
      title2: "VIP experience.",
      sub: "Four packages, limited quantities. All purchases are completed securely on eventcartel.com.",
      perGuest: "per guest",
      qty: "packages available",
      buy: "Buy Package",
      popular: "Best value",
      compareTitle: "Quick comparison",
      compareEvent: "Event",
      compareRows: [
        "New Year Gala 2027 · Kalos · Dec 31",
        "After Party / First Sunrise · Villa Aura · Jan 1",
        "First Sunset of 2027 · Villa Aura · Jan 1",
        "Diamond VIP Privileges",
      ],
    },
    footer: {
      dates: "Dec 28 · 2026 — Jan 3 · 2027 · Saint Martin",
      seeYou1: "See you on",
      seeYou2: "the sand.",
      buy: "Buy Tickets",
      events: "Events",
      follow: "Follow",
      contact: "Contact",
      gala: "New Year's Eve Gala",
      afterparty: "Official After Party",
      sunset: "First Sunset",
      rights: "© 2026 Vse Nashi Music Festival SXM. All rights reserved.",
      tickets: "Tickets are sold via eventcartel.com",
    },
    event: {
      all: "All events",
      back: "Back home",
      notFound: "Event not found",
      kicker: "The Night",
      about: "About the evening",
      date: "Date",
      time: "Time",
      venue: "Venue",
      dress: "Dress code",
      lineupKicker: "Lineup",
      lineup: "Who's playing",
      programKicker: "Program",
      program: "The evening flow",
      galleryKicker: "Atmosphere",
      gallery: "Gallery",
      gettingThere: "Getting there",
      theVenue: "The venue",
      buy: "Buy Tickets",
    },
  },
  ru: {
    nav: { home: "Главная", gala: "Гала", afterparty: "Афтепати", sunset: "Первый закат", buy: "Купить билеты" },
    hero: {
      kicker: "28 декабря 2026 — 3 января 2027 · Сен-Мартен",
      lines: ["Vse Nashi Saint Martin", "New Year Celebration"],
      sub: "Presented by America Bez Granitz TV",
    },
    marquee: ["New Year Gala 2027", "Официальное афтепати", "Первый рассвет", "Первый закат", "Шесть ивентов · Семь дней", "Сен-Мартен", "Vse Nashi SXM"],
    countdown: { title: "До гала осталось", days: "Дней", hours: "Часов", min: "Мин", sec: "Сек" },
    manifesto: {
      kicker: "Почему Сен-Мартен",
      title1: "Встречайте Новый год",
      titleAccent: "так, как",
      title2: "никогда раньше.",
      chapters: [
        {
          title: "Остров",
          text: "Сен-Мартен — это место, где самолёты садятся прямо над пляжем Махо, где французская кухня встречается с карибским ритмом, а декабрь ощущается как идеальный июль. Мы выбрали его, потому что ни один зал в мире не сравнится с этим берегом.",
        },
        {
          title: "Звук",
          text: "Иракли — «Лондон–Париж», «Капли абсента» и специальное новогоднее выступление. Johnny Versace — Afrobeats, dance и мировые хиты. Douglas — легендарный итальянский певец, DJ Dr. Rayham, DJ Kasbi и специальные гости. Один непрерывный саундтрек вашего Нового года.",
        },
        {
          title: "Ритуал",
          text: "Moët & Chandon и фейерверк над морем в полночь в Kalos. Первый рассвет 2027 года на Villa Aura с шампанским в руке. И первый закат года у бассейна. Это не вечеринка, на которую приходят, — это то, как вы запомните начало года.",
        },
      ],
    },
    events: {
      kicker: "Программа",
      title1: "Signature",
      title2: "events.",
      onePass: "Один билет · Все ивенты",
      view: "Подробнее",
      more: "…и еще специальные ивенты на лучших площадках острова — объявим совсем скоро.",
    },
    finalCta: { kicker: "Количество мест ограничено", title1: "Встречайте 2027 там,", titleAccent: "где встаёт солнце.", buy: "Купить билеты" },
    packages: {
      kicker: "Билеты и пакеты",
      title1: "Выберите свой",
      title2: "VIP-опыт.",
      sub: "Четыре пакета, количество ограничено. Оплата проходит безопасно на eventcartel.com.",
      perGuest: "за гостя",
      qty: "пакетов",
      buy: "Купить пакет",
      popular: "Максимум впечатлений",
      compareTitle: "Быстрое сравнение",
      compareEvent: "Ивент",
      compareRows: [
        "New Year Gala 2027 · Kalos · 31 дек",
        "After Party / First Sunrise · Villa Aura · 1 янв",
        "First Sunset of 2027 · Villa Aura · 1 янв",
        "Привилегии Diamond VIP",
      ],
    },
    footer: {
      dates: "28 дек · 2026 — 3 янв · 2027 · Сен-Мартен",
      seeYou1: "Увидимся",
      seeYou2: "на песке.",
      buy: "Купить билеты",
      events: "Ивенты",
      follow: "Соцсети",
      contact: "Контакты",
      gala: "Новогодний гала",
      afterparty: "Официальное афтепати",
      sunset: "Первый закат",
      rights: "© 2026 Vse Nashi Music Festival SXM. Все права защищены.",
      tickets: "Билеты продаются на eventcartel.com",
    },
    event: {
      all: "Все ивенты",
      back: "На главную",
      notFound: "Ивент не найден",
      kicker: "Вечер",
      about: "О вечере",
      date: "Дата",
      time: "Время",
      venue: "Место",
      dress: "Дресс-код",
      lineupKicker: "Лайнап",
      lineup: "Кто выступает",
      programKicker: "Программа",
      program: "Программа вечера",
      galleryKicker: "Атмосфера",
      gallery: "Галерея",
      gettingThere: "Как добраться",
      theVenue: "Площадка",
      buy: "Купить билеты",
    },
  },
};
